import React from 'react';
import { Box, Typography, Stack, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';
import DynamicPieChart from './DynamicPieChart';
import BarChartIcon from '../icons/BarChartIcon';

// Mobile-First Styled Components
const StatsWrapper = styled(Paper)(({ theme }) => ({
  borderRadius: '12px',
  border: '1px solid #E2E8F0',
  padding: '16px',
  backgroundColor: '#ffffff',
  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  [theme.breakpoints.up('sm')]: {
    padding: '20px',
    border: 'none',
    boxShadow: 'none',
    backgroundColor: 'transparent',
  },
}));

const StatsContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: '12px',
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '16px',
  },
  [theme.breakpoints.up('md')]: {
    display: 'flex',
    flexDirection: 'row',
    gap: '13px',
    flexWrap: 'wrap',
  },
}));

const StatCard = styled(Stack)(({ theme }) => ({
  alignItems: 'center',
  gap: '6px',
  padding: '12px 8px',
  backgroundColor: '#F8FAFC',
  borderRadius: '10px',
  border: '1px solid #E2E8F0',
  transition: 'all 0.2s ease',
  '&:active': {
    transform: 'scale(0.98)',
    backgroundColor: '#F1F5F9',
  },
  [theme.breakpoints.up('sm')]: {
    padding: '16px 12px',
    gap: '4px',
    backgroundColor: 'transparent',
    border: 'none',
    minWidth: '100px',
    '&:active': {
      transform: 'none',
    },
  },
  [theme.breakpoints.up('md')]: {
    minWidth: '120px',
    flex: '0 0 auto',
  },
}));

const StatLabel = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '11px',
  fontWeight: 500,
  color: '#64748b',
  textAlign: 'center',
  textTransform: 'uppercase',
  letterSpacing: '0.3px',
  [theme.breakpoints.up('sm')]: {
    fontSize: '12px',
    fontWeight: 400,
    color: '#121927',
    textTransform: 'none',
    letterSpacing: 'normal',
  },
}));

const StatValue = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '20px',
  fontWeight: 700,
  color: '#0B91D6',
  textAlign: 'center',
  [theme.breakpoints.up('sm')]: {
    fontSize: '22px',
    fontWeight: 600,
    color: '#121927',
  },
}));

const IconContainer = styled(Box)(({ theme }) => ({
  width: '36px',
  height: '36px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  [theme.breakpoints.up('sm')]: {
    width: '40px',
    height: '40px',
  },
}));

const HeatmapStatsSection = ({ stats }) => {
  const statsData = [
    {
      label: 'Good',
      value: `${stats.good}%`,
      icon: <DynamicPieChart percentage={stats.good} color="#34A853" size={36} />
    },
    {
      label: 'Poor',
      value: `${stats.poor}%`,
      icon: <DynamicPieChart percentage={stats.poor} color="#EF232A" size={36} />
    },
    {
      label: 'Average',
      value: `${stats.average}%`,
      icon: <DynamicPieChart percentage={stats.average} color="#FBBC05" size={36} />
    },
    {
      label: 'Avg Rank',
      value: stats.averageRank != null && stats.averageRank !== '—' ? stats.averageRank : '—',
      icon: <BarChartIcon width={32} height={24} />
    },
    {
      label: 'Voice',
      value: `${stats.shareOfVoice}%`,
      icon: <DynamicPieChart percentage={stats.shareOfVoice} color="#0B91D6" size={36} />
    },
    {
      label: 'Out Top20',
      value: `${stats.outOfTop20}%`,
      icon: <DynamicPieChart percentage={stats.outOfTop20} color="#6B7280" size={36} />
    }
  ];

  return (
    <StatsWrapper elevation={0}>
      <StatsContainer>
        {statsData.map((stat, index) => (
          <StatCard key={index}>
            <StatLabel>{stat.label}</StatLabel>
            <StatValue>{stat.value}</StatValue>
            <IconContainer>
              {stat.icon}
            </IconContainer>
          </StatCard>
        ))}
      </StatsContainer>
    </StatsWrapper>
  );
};

export default HeatmapStatsSection;